import React, { useRef } from "react";
import { Flex, LabelWrapper } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import { SelectableSearchMenu } from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/SelectableSearchMenu";
import { GetDoc } from "@/Utils/StateLib";
/**
 * AssignmentSection Component
 * Allows assigning and removing employees to/from positions
 */
const AssignmentSection = ({
  assignedEmployees = [],
  onAssignEmployee,
  onRemoveEmployee,
  dataTestIdPrefix = "position-assignment",
  padding = "16px",
  disabled = false
}) => {
  const searchHandleRef = useRef(null);

  // Map assigned employees to the format expected by SelectableSearchMenu
  const mappedAssignedEmployees = assignedEmployees.map(item => ({
    id: item.SmartGetFieldValue("EmployeeID"),
    label: sanitizeBlockedValue(item.SmartGetFieldValue("Name")),
    value: item.SmartGetFieldValue("EmployeeID"),
    // Keep a reference to the original employee object for removal
    originalEmployee: item
  }));

  const handleAssign = target => {
    if (target) {
      onAssignEmployee(target.result);
      // Clear the search input if needed
      if (searchHandleRef.current?.setValue) {
        searchHandleRef.current.setValue("");
      }
    }
  };

  const handleRemove = target => {
    // Find the original employee object from the mapped item
    const employeeToRemove =
      target.originalEmployee ||
      assignedEmployees.find(
        emp =>
          emp.SmartGetFieldValue("EmployeeID") === target.id ||
          emp.SmartGetFieldValue("EmployeeID") === target.value
      );

    if (!employeeToRemove) {
      console.warn("Could not find employee to remove", target);
      return;
    }

    onRemoveEmployee(employeeToRemove);
  };

  const getProjectFields = () => {
    const projectFieldsIds = [
      "Name",
      "Title",
      "Assignment Status",
      "Employee Salary",
      "EmployeeID",
      "Employee PositionID"
    ];
    const doc = GetDoc();
    const objDB = doc.GetObjDB();
    const objClass = objDB.GetClassByName("Box");
    let projectFieldNames = [];
    for (const id of projectFieldsIds) {
      const fD = objClass.GetFieldDefByID(id);
      if (fD) {
        projectFieldNames.push(fD.GetFieldName());
      }
    }
    return projectFieldNames;
  };

  return (
    <Flex
      className={"ocn-list-scrollbar"}
      direction={"column"}
      padding={padding}
      gap={"16px"}
      height={"100%"}
      overflow={"auto"}
      dataTestId={`${dataTestIdPrefix}-section`}
    >
      <LabelWrapper label={appstr("Assign employee")} labelSize="medium">
        <Flex direction="row" gap="8px" alignItems="flex-end">
          <div style={{ flexGrow: 1, padding: "2px" }}>
            <SelectableSearchMenu
              dataTestId={`${dataTestIdPrefix}-employee-search`}
              fullWidth
              inputSize={"medium"}
              disabled={disabled}
              displayRecents={true}
              selectedItem={mappedAssignedEmployees}
              expandable={mappedAssignedEmployees.length > 1}
              onRemoveItem={disabled ? null : handleRemove}
              searchMenuProps={{
                clearOnSelect: true,
                clearable: true,
                placeholder: appstr("Search for an employee"),
                expandWidth: true,
                searchProps: {
                  handleRef: searchHandleRef
                }
              }}
              handleSearchOptionClick={disabled ? null : handleAssign}
              queryOptions={{
                includeAux: false,
                queryType: "person",
                includeDeletes: true
              }}
              projectFields={getProjectFields()}
              handlePositions={false}
              showAssignmentStatus={true}
            />
          </div>
        </Flex>
      </LabelWrapper>
    </Flex>
  );
};

export default AssignmentSection;
